import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '../App';
import { MessageCircle, Phone, Video, Calendar, Heart, LogOut, Clock, CheckCircle, XCircle, ChevronRight, ShieldCheck, FileText } from 'lucide-react';

// MVP Components
import WellnessSection from '../components/WellnessSection';
import HelplineButton from '../components/HelplineButton';
import DigitalAssistantSection from '../components/DigitalAssistantSection';

export default function SeekerDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);
  const [sessions, setSessions] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [intake, setIntake] = useState(null);
  const [intakeChecked, setIntakeChecked] = useState(false);
  const pollInterval = useRef(null);

  useEffect(() => {
    fetchData();
    fetchIntake();

    // Poll for updates every 2 seconds for real-time feel
    pollInterval.current = setInterval(fetchData, 2000);

    return () => {
      if (pollInterval.current) {
        clearInterval(pollInterval.current);
      }
    };
  }, []);

  const fetchIntake = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/seeker/intake`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setIntake(res.data);
    } catch (e) {
      if (e.response?.status === 404) setIntake(null);
    } finally {
      setIntakeChecked(true);
    }
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [sessionsRes, appointmentsRes] = await Promise.all([
        axios.get(`${API}/sessions`, { headers }),
        axios.get(`${API}/appointments`, { headers })
      ]);
      setSessions(sessionsRes.data);
      setAppointments(appointmentsRes.data);
    } catch (error) {
      console.error('Fetch data error:', error);
      // Don't show toast on every poll failure
      if (loading) {
        toast.error('Failed to fetch data');
      }
    } finally {
      setLoading(false);
    }
  };

  const startSession = async (sessionType) => {
    try {
      const response = await axios.post(`${API}/sessions`, {
        session_type: sessionType,
        provider_id: null
      });

      if (sessionType === 'anonymous_chat') {
        navigate(`/chat/${response.data.id}`);
      } else if (sessionType === 'anonymous_voice') {
        // Use the VideoCallPage which has 100ms integration for voice calls
        navigate(`/call/${response.data.id}`);
      } else {
        toast.success('Session created! Please book an appointment.');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to create session');
    }
  };

  // Resume Care Seeker intent captured on the bridge screen (chat / voice).
  useEffect(() => {
    const intent = sessionStorage.getItem('veyonika_intent');
    if (intent === 'chat' || intent === 'voice') {
      sessionStorage.removeItem('veyonika_intent');
      startSession(intent === 'voice' ? 'anonymous_voice' : 'anonymous_chat');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const serviceCards = [
    {
      title: 'Private Chat',
      description: 'Start your first private chat with a care provider',
      icon: MessageCircle,
      type: 'anonymous_chat',
      color: '#7D202B',
      action: 'Start a private chat',
      testId: 'start-anonymous-chat-btn'
    },
    {
      title: 'Voice Call',
      description: 'Connect through a secure, private voice call',
      icon: Phone,
      type: 'anonymous_voice',
      color: '#963A42',
      action: 'Start a voice call',
      testId: 'start-anonymous-voice-btn'
    },
    {
      title: 'Book Therapy Session',
      description: 'Schedule with a certified therapist',
      icon: Video,
      type: 'therapy',
      color: '#DCA593',
      action: 'Book Appointment',
      testId: 'book-therapy-btn'
    }
  ];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle size={16} className="text-success" />;
      case 'cancelled':
        return <XCircle size={16} className="text-destructive" />;
      case 'active':
      case 'confirmed':
        return <Clock size={16} className="text-info" />;
      default:
        return <Clock size={16} className="text-warning" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6] theme-seeker overflow-x-hidden">
      {/* Role Indicator Banner - Mobile Optimized */}
      <div className="bg-[#7D202B] text-white py-2 px-2 sm:px-4 text-center sticky top-0 z-[60]">
        <div className="container mx-auto flex items-center justify-center gap-1 sm:gap-3 flex-wrap">
          <span className="bg-white text-[#7D202B] font-bold px-2 py-0.5 rounded-full text-xs sm:text-sm">CS</span>
          <span className="font-medium text-xs sm:text-sm">Care Seeker</span>
          <span className="text-white/80 text-xs truncate max-w-[120px] sm:max-w-none">{user?.email}</span>
        </div>
      </div>
      
      {/* Header with Seeker Theme - Mobile Optimized */}
      <header className="dashboard-header text-white sticky top-[32px] sm:top-[40px] z-50">
        <div className="container mx-auto px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Heart className="text-white" size={24} />
            <h1 className="text-base sm:text-xl font-heading font-semibold hidden sm:block">Veyonika</h1>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <span className="text-xs sm:text-sm text-white/80">Hi, {user?.name?.split(' ')[0]}</span>
            <button
              data-testid="logout-btn"
              onClick={logout}
              className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-3 sm:px-6 py-6 sm:py-12">
        {/* Welcome Section - Mobile Optimized */}
        <div className="mb-6 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-semibold text-[#2D3748] mb-2">
            Welcome back, {user?.name?.split(' ')[0]}
          </h2>
          <p className="text-base sm:text-lg text-[#718096]">How can we support you today?</p>
        </div>

        {/* Intake banner — gentle nudge if pre-registration isn't done */}
        {intakeChecked && (
          <button
            type="button"
            onClick={() => navigate('/seeker-intake')}
            className={`w-full mb-6 sm:mb-8 p-4 sm:p-5 rounded-2xl border flex items-center gap-4 text-left transition-shadow hover:shadow-md ${
              intake
                ? 'bg-gradient-to-r from-emerald-50 to-white border-emerald-100'
                : 'bg-gradient-to-r from-pink-50 to-purple-50 border-purple-100'
            }`}
            data-testid="intake-banner"
          >
            <div
              className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${
                intake ? 'bg-emerald-100 text-emerald-700' : 'bg-purple-100 text-purple-700'
              }`}
            >
              {intake ? <CheckCircle size={22} /> : <ShieldCheck size={22} />}
            </div>
            <div className="flex-1 min-w-0">
              {intake ? (
                <>
                  <div className="text-sm font-medium text-emerald-800">Intake complete</div>
                  <div className="text-xs text-emerald-700/80 mt-0.5">
                    Your ID: <span className="font-mono">{intake.masked_id}</span> • Tap to update
                  </div>
                </>
              ) : (
                <>
                  <div className="text-sm font-medium text-purple-800">Complete your quick intake</div>
                  <div className="text-xs text-purple-700/80 mt-0.5">
                    Takes a minute — helps your counsellor prepare. You'll get your ID at the end.
                  </div>
                </>
              )}
            </div>
            <ChevronRight size={20} className={intake ? 'text-emerald-700' : 'text-purple-700'} />
          </button>
        )}

        {/* Shared Resources quick link */}
        <button
          type="button"
          onClick={() => navigate('/my-resources')}
          data-testid="shared-resources-link"
          className="w-full mb-6 sm:mb-8 p-4 rounded-2xl border border-[#FAC7C0]/70 bg-white flex items-center gap-4 text-left transition-shadow hover:shadow-md"
        >
          <div className="w-11 h-11 rounded-full bg-[#FAC7C0]/40 text-[#7D202B] flex items-center justify-center shrink-0">
            <FileText size={22} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-[#3B2923]">Shared resources</div>
            <div className="text-xs text-[#7A625C] mt-0.5">View PDFs your care team has shared with you</div>
          </div>
          <ChevronRight size={20} className="text-[#7D202B]" />
        </button>

        {/* Service Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 mb-8 sm:mb-12">
          {serviceCards.map((service, index) => {
            const Icon = service.icon;
            return (
              <div key={index} className="card p-6">
                <div
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-4"
                  style={{ backgroundColor: `${service.color}20` }}
                >
                  <Icon size={28} style={{ color: service.color }} />
                </div>
                <h3 className="text-xl font-heading font-semibold text-[#2D3748] mb-2">
                  {service.title}
                </h3>
                <p className="text-[#718096] mb-4">{service.description}</p>
                <button
                  data-testid={service.testId}
                  onClick={() => {
                    if (service.type === 'therapy') {
                      navigate('/book-appointment');
                    } else {
                      startSession(service.type);
                    }
                  }}
                  className="btn-primary w-full"
                  style={{ backgroundColor: service.color }}
                >
                  {service.action}
                </button>
              </div>
            );
          })}
        </div>

        {/* Recent Sessions */}
        <div className="mb-8">
          <h3 className="text-2xl font-heading font-semibold text-[#2D3748] mb-4">Recent Sessions</h3>
          {sessions.length === 0 ? (
            <div className="card p-8 text-center text-[#718096]">
              <MessageCircle size={48} className="mx-auto mb-4 opacity-30" />
              <p>No sessions yet.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {sessions.slice(0, 5).map((session) => {
                const isChat = session.channel === 'chat' || session.session_type === 'anonymous_chat';
                const isVoice = session.channel === 'voice' || session.session_type === 'anonymous_voice';
                const isVideo = session.channel === 'video';
                const canJoin = ['pending', 'queued', 'active'].includes(session.status);
                
                return (
                  <div key={session.id} data-testid={`session-${session.id}`} className="card p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {getStatusIcon(session.status)}
                        <div>
                          <h4 className="font-medium text-[#2D3748] capitalize">
                            {session.grade_code ? `${session.grade_code} Session` : (session.session_type?.replace('_', ' ') || 'Session')}
                          </h4>
                          <p className="text-sm text-[#718096]">
                            {new Date(session.created_at).toLocaleDateString()} • {session.channel || 'chat'}
                          </p>
                          {session.mood_tags && session.mood_tags.length > 0 && (
                            <div className="flex gap-1 mt-1 flex-wrap">
                              {session.mood_tags.map(tag => (
                                <span key={tag} className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded-full capitalize">
                                  {tag.replace('_', ' ')}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm px-3 py-1 rounded-full bg-primary/10 text-primary capitalize">
                          {session.status}
                        </span>
                        {canJoin && isChat && (
                          <button
                            onClick={() => navigate(`/chat/${session.id}`)}
                            className="btn-secondary text-sm flex items-center gap-1"
                          >
                            <MessageCircle size={14} />
                            Continue Chat
                          </button>
                        )}
                        {canJoin && (isVoice || isVideo) && (
                          <button
                            onClick={() => navigate(`/call/${session.id}`)}
                            className="btn-primary text-sm flex items-center gap-1"
                          >
                            {isVoice ? <Phone size={14} /> : <Video size={14} />}
                            Join Call
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Upcoming Appointments */}
        <div>
          <h3 className="text-2xl font-heading font-semibold text-[#2D3748] mb-4">Upcoming Appointments</h3>
          {appointments.filter(a => a.status !== 'completed' && a.status !== 'cancelled').length === 0 ? (
            <div className="card p-8 text-center text-[#718096]">
              <Calendar size={48} className="mx-auto mb-4 opacity-30" />
              <p>No upcoming appointments</p>
            </div>
          ) : (
            <div className="space-y-4">
              {appointments
                .filter(a => a.status !== 'completed' && a.status !== 'cancelled')
                .sort((a, b) => {
                  const statusOrder = { 'in_progress': 0, 'confirmed': 1, 'scheduled': 2 };
                  return statusOrder[a.status] - statusOrder[b.status];
                })
                .map((appointment) => (
                  <div key={appointment.id} data-testid={`appointment-${appointment.id}`} className="card p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <Calendar className="text-primary" size={24} />
                        <div>
                          <h4 className="font-medium text-[#2D3748] capitalize">
                            {appointment.grade_code ? `${appointment.grade_code} Session` : (appointment.session_type?.replace('_', ' ') || '')} Session
                          </h4>
                          <p className="text-sm text-[#718096]">
                            {appointment.scheduled_date} at {appointment.scheduled_time}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className={`text-sm px-3 py-1 rounded-full capitalize ${
                          appointment.status === 'in_progress' 
                            ? 'bg-green-100 text-green-700 animate-pulse' 
                            : 'bg-primary/10 text-primary'
                        }`}>
                          {appointment.status === 'in_progress' ? 'Session Active' : appointment.status}
                        </span>
                        <span className="font-semibold text-primary">
                          ₹{appointment.amount}
                        </span>
                      </div>
                    </div>
                    {appointment.status === 'in_progress' && appointment.session_id && (
                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <button
                          data-testid={`join-session-${appointment.id}`}
                          onClick={() => navigate(`/call/${appointment.session_id}`)}
                          className="btn-primary flex items-center gap-2 bg-green-600 hover:bg-green-700"
                        >
                          <Video size={18} />
                          Join Video Session
                        </button>
                        <p className="text-sm text-green-600 mt-2">
                          Your provider has started the session. Click to join now!
                        </p>
                      </div>
                    )}
                    {appointment.status === 'confirmed' && (
                      <div className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-2">
                        <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                        <p className="text-sm text-[#718096]">
                          Appointment confirmed. Waiting for your provider to start the session...
                        </p>
                      </div>
                    )}
                    {appointment.status === 'scheduled' && (
                      <div className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-2">
                        <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                        <p className="text-sm text-[#718096]">
                          Pending confirmation from provider
                        </p>
                      </div>
                    )}
                  </div>
                ))}
            </div>
          )}
        </div>
        
        {/* Past Appointments */}
        {appointments.filter(a => a.status === 'completed').length > 0 && (
          <div className="mt-8">
            <h3 className="text-2xl font-heading font-semibold text-[#2D3748] mb-4">Past Appointments</h3>
            <div className="space-y-4">
              {appointments
                .filter(a => a.status === 'completed')
                .slice(0, 5)
                .map((appointment) => (
                  <div key={appointment.id} className="card p-6 opacity-75">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <CheckCircle className="text-green-500" size={24} />
                        <div>
                          <h4 className="font-medium text-[#2D3748] capitalize">
                            {appointment.grade_code ? `${appointment.grade_code} Session` : (appointment.session_type?.replace('_', ' ') || '')} Session
                          </h4>
                          <p className="text-sm text-[#718096]">
                            {appointment.scheduled_date} at {appointment.scheduled_time}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-sm px-3 py-1 rounded-full bg-green-100 text-green-700 capitalize">
                          Completed
                        </span>
                        <span className="font-semibold text-[#718096]">
                          ₹{appointment.amount}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Helpline Button */}
        <div className="mb-8 flex justify-center">
          <HelplineButton maskedId={user?.masked_id} />
        </div>

        {/* Digital Assistant Section */}
        <div className="mb-8">
          <DigitalAssistantSection />
        </div>

        {/* Wellness & Gamification Section */}
        <div className="mb-8">
          <WellnessSection maskedId={user?.masked_id} />
        </div>
      </div>
    </div>
  );
}